import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faClapperboardPlay } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Card, Divider, Grid } from '@mui/material'
import { ChangeEvent, FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { getErrorMessage } from '@/api/utils/get-error'
import { useVideosPackageDelete } from '@/api/videos/packages/delete'
import { useVideosPackage } from '@/api/videos/packages/get'
import LayoutPageContent from '@/common/components/Layout/LayoutPageContent'
import PageHeader from '@/common/components/Layout/LayoutPageContent/PageHeader'
import LoadingContainer from '@/common/components/LoadingContainer/LoadingContainer'
import QuizPreview from '@/common/components/QuizPreview'
import MainTab from '@/common/components/Tabs/MainTab'
import MainTabPage from '@/common/components/Tabs/MainTabPage'
import MainTabs from '@/common/components/Tabs/MainTabs'
import MainTabsWrapper from '@/common/components/Tabs/MainTabsWrapper'
import VideoPreview from '@/common/components/VideoPreview'
import useToast from '@/common/hooks/useToast'
import { theme } from '@/theme/theme'
import DeletePackagePopup from '../../SimulationsLibrary/DeletePackagePopup'
import Summary from './Summary'
import LaunchWizard from '@/common/components/LaunchWizard/LaunchWizard'

const VideosPackagePage: FC = () => {
  const { t } = useTranslation()
  const { id = '' } = useParams<'id'>()
  const [launchWizardIsOpen, setLaunchWizardIsOpen] = useState(false)
  const { mutateAsync: deleteVideosPackage } = useVideosPackageDelete()
  const [isDeletePopUpOpen, setIsDeletePopUpOpen] = useState(false)
  const { data: videosPackage, isPending } = useVideosPackage(id)

  const [currentTab, setCurrentTab] = useState(0)
  const { errorToast, successToast } = useToast()
  const navigate = useNavigate()

  const handleTabChange = useCallback(
    (_: ChangeEvent<{}>, index: number) => {
      setCurrentTab(index)
    },
    [setCurrentTab]
  )

  const handlePackageDelete = async () => {
    try {
      await deleteVideosPackage(videosPackage?.id)
      successToast(t('simulationPackage.deleteSuccess'))
      navigate('/content-library/videos')
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      errorToast(errorMessage)
    }
  }

  if (isPending || !videosPackage) return <LoadingContainer />

  return (
    <LayoutPageContent>
      <PageHeader>
        <Button
          onClick={() => {
            setLaunchWizardIsOpen(true)
          }}
          variant="contained">
          {t('videosPackage.header.launch')}
        </Button>
      </PageHeader>

      <Grid item xs={12}>
        <Summary
          openDeletePopup={() => {
            setIsDeletePopUpOpen(true)
          }}
          videosPackage={videosPackage}></Summary>
      </Grid>

      <Grid item xs={12}>
        <MainTabsWrapper>
          <MainTabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons={'auto'}>
            {videosPackage?.videos.map((video: any, index: number) => (
              <MainTab
                key={index}
                label={video.title}
                icon={<FontAwesomeIcon icon={faClapperboardPlay as IconProp} fontSize={18} />}
                iconPosition="start"
              />
            ))}
          </MainTabs>
        </MainTabsWrapper>
        {videosPackage?.videos.map((video: any, index: number) => (
          <MainTabPage key={video.id} hidden={currentTab !== index}>
            <Card sx={{ minWidth: '500px' }}>
              <VideoPreview video={video} />
            </Card>

            {video.number_of_quiz_questions > 0 && (
              <Box>
                <Divider sx={{ margin: theme.spacing(0, 4) }} />
                <Card sx={{ minWidth: '500px' }}>
                  <QuizPreview video={video} />
                </Card>
              </Box>
            )}
          </MainTabPage>
        ))}
      </Grid>

      <LaunchWizard
        open={launchWizardIsOpen}
        onClose={() => setLaunchWizardIsOpen(false)}
        assets={videosPackage.videos}
        packageName={videosPackage.name}
        packageId={videosPackage.id}
      />
      <DeletePackagePopup
        open={isDeletePopUpOpen}
        onClose={() => {
          setIsDeletePopUpOpen(false)
        }}
        onConfirm={() => {
          handlePackageDelete()
          setIsDeletePopUpOpen(false)
        }}
        packageName={videosPackage?.name}
      />
    </LayoutPageContent>
  )
}

export default VideosPackagePage
